import type { Marking } from './schema'
import { faFilePen } from '@fortawesome/pro-duotone-svg-icons'
import {
  NOTE_INDICATOR_BUTTON_SIZE,
  NOTE_INDICATOR_GAP,
  NOTE_INDICATOR_LINE_HEIGHT,
} from '~/constants/markings'
import { markElementsFor } from './dom'

// Duotone icons expose `.icon[4]` as a `[secondaryPath, primaryPath]` tuple,
// other styles a single string — normalize to one <path> per layer.
const NOTE_INDICATOR_ICON = (() => {
  const [width, height, , , raw] = faFilePen.icon
  const paths = Array.isArray(raw) ? raw : [raw]
  return { width, height, paths }
})()

const NOTE_INDICATOR_FOR_ATTR = 'data-marking-note-indicator-for'

// Opens the MarkingNoteTooltip for the marking id in the event detail.
export const NOTE_INDICATOR_OPEN_EVENT = 'marking:open-note'

export function dispatchNoteIndicatorOpen(detail: NoteIndicatorOpenDetail): void {
  document.dispatchEvent(new CustomEvent(NOTE_INDICATOR_OPEN_EVENT, { detail }))
}

export function isNoteIndicatorOpenEvent(event: Event): event is CustomEvent<NoteIndicatorOpenDetail> {
  return event instanceof CustomEvent && typeof event.detail?.id === 'string'
}

export interface NoteIndicatorOpenDetail {
  id: string
  clientX: number
  clientY: number
  /** Inline record for markings not yet reflected in the shared store. */
  marking?: Marking
}

/**
 * Adds or removes the clickable note indicator in the margin of the line
 * where the marking starts. Appended to the nearest block ancestor (not the
 * <mark>) so it sits at the line's edge regardless of where the mark begins.
 *
 * Nothing is placed when the layout has no margin to place it in — narrow
 * screens, the commentary column, or a table wider than its container. Forcing
 * it in put the button on top of the text, to the left of it, or outside the
 * scroll container, where it added horizontal scrolling. Marked text opens its
 * own note on click, so the note stays reachable either way.
 */
export function setMarkingNoteIndicator(id: string, hasNote: boolean, elements: ArrayLike<HTMLElement> = markElementsFor(id)): void {
  const existing = document.querySelector<HTMLElement>(`[${NOTE_INDICATOR_FOR_ATTR}="${CSS.escape(id)}"]`)

  if (!hasNote) {
    existing?.remove()
    return
  }

  const mark = elements[0]

  if (!mark) {
    return
  }

  const block = nearestBlockAncestor(mark)

  if (!block || marginSpaceBesideBlock(block) < NOTE_INDICATOR_BUTTON_SIZE + NOTE_INDICATOR_GAP) {
    existing?.remove()
    return
  }

  if (existing?.parentElement === block) {
    positionNoteIndicator(existing, mark, block)
    return
  }

  existing?.remove()
  ensurePositionedContext(block)
  const indicator = createNoteIndicator(id)
  block.appendChild(indicator)
  positionNoteIndicator(indicator, mark, block)
}

function ensurePositionedContext(el: HTMLElement): void {
  if (getComputedStyle(el).position === 'static') {
    el.style.position = 'relative'
  }
}


function positionNoteIndicator(indicator: HTMLElement, mark: HTMLElement, block: HTMLElement): void {
  const markRect = mark.getBoundingClientRect()
  const blockRect = block.getBoundingClientRect()
  // Center the button on the mark's first line rather than pinning its top.
  let top = markRect.top - blockRect.top - (NOTE_INDICATOR_BUTTON_SIZE - NOTE_INDICATOR_LINE_HEIGHT) / 2

  // Multiple noted markings on the same line would stack their indicators on
  // the same spot — stagger later ones downward so each stays visible.
  const siblings = Array.from(block.querySelectorAll<HTMLElement>(`[${NOTE_INDICATOR_FOR_ATTR}]`))
    .filter(el => el !== indicator)

  while (siblings.some(el => Math.abs(Number.parseFloat(el.style.top) - top) < NOTE_INDICATOR_BUTTON_SIZE)) {
    top += NOTE_INDICATOR_BUTTON_SIZE + NOTE_INDICATOR_GAP
  }

  indicator.style.top = `${top}px`

  // Caller has already confirmed the button fits beside the block, so this
  // stays inside the scroll container's content box. An absolutely positioned
  // child sticking out would widen the scrollable area (horizontal scrollbar
  // in Safari).
  indicator.style.left = `${blockRect.width + NOTE_INDICATOR_GAP}px`
}

/**
 * Free space between the block's right edge and the scroll container's content
 * box. Returns 0 when the block is not inside one, since the bounds the button
 * must stay within are then unknown.
 */
function marginSpaceBesideBlock(block: HTMLElement): number {
  const container = block.closest<HTMLElement>('[data-container]')

  if (!container) {
    return 0
  }

  const blockRect = block.getBoundingClientRect()
  const containerRect = container.getBoundingClientRect()
  return containerRect.left + container.clientWidth - blockRect.right
}

function nearestBlockAncestor(el: Element): HTMLElement | null {
  let current: Element | null = el.parentElement

  while (current && current !== document.body) {
    const display = getComputedStyle(current).display

    if (
      display === 'block'
      || display === 'list-item'
      || display === 'flow-root'
      || display === 'flex'
      || display === 'grid'
    ) {
      return current instanceof HTMLElement ? current : null
    }

    current = current.parentElement
  }

  return null
}

function createNoteIndicator(id: string): HTMLElement {
  const button = document.createElement('button')
  button.type = 'button'
  button.setAttribute(NOTE_INDICATOR_FOR_ATTR, id)
  button.setAttribute('aria-label', 'Vis notat')
  button.className = 'absolute inline-flex items-center justify-center size-7 rounded-md cursor-pointer bg-transparent border-0 p-0 text-carbon-500 hover:bg-carbon-100 dark:text-white dark:hover:bg-carbon-400'
  // Raw SVG (exception to the Icon rule): this runs in a plain DOM-mutation
  // path where mounting a Vue component isn't possible.
  const { width, height, paths } = NOTE_INDICATOR_ICON
  const layers = paths.length === 2
    ? `<path opacity="0.4" d="${paths[0]}"/><path d="${paths[1]}"/>`
    : `<path d="${paths[0]}"/>`
  button.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}" width="20" height="20" fill="currentColor">${layers}</svg>`
  button.addEventListener('click', (event) => {
    event.stopPropagation()
    const detail: NoteIndicatorOpenDetail = {
      id,
      clientX: event.clientX,
      clientY: event.clientY,
    }
    dispatchNoteIndicatorOpen(detail)
  })
  return button
}
